import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { 
  Users, 
  Search,
  ChevronLeft,
  Trophy,
  Trash2
} from 'lucide-react';
import { formatDateTime } from '../lib/utils';

export default function AdminTeams() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }

    fetchTeams();
  }, [user, navigate]);

  const fetchTeams = async () => {
    try {
      const response = await api.get('/admin/teams');
      setTeams(response.data || []);
    } catch (error) {
      console.error('Failed to fetch teams:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (teamId, teamName) => {
    if (!window.confirm(`Team "${teamName}" wirklich löschen?`)) return;

    try {
      await api.delete(`/admin/teams/${teamId}`);
      toast.success('Team gelöscht');
      await fetchTeams();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const filteredTeams = teams.filter(t =>
    t.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Skeleton className="h-8 w-64 mb-8" />
          <Skeleton className="h-96 rounded-2xl" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-teams">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-display font-bold text-2xl text-tunis-blue">
              Teams verwalten
            </h1>
            <p className="text-muted-foreground">{teams.length} registrierte Teams</p>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder="Team suchen..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-12"
          />
        </div>

        {/* Table */}
        <Card className="border-slate-200">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Team</TableHead>
                  <TableHead>Kapitän</TableHead>
                  <TableHead className="text-center">Mitglieder</TableHead>
                  <TableHead className="text-center">ELO</TableHead>
                  <TableHead className="text-center">S/N/U</TableHead>
                  <TableHead className="text-center">Turniersiege</TableHead>
                  <TableHead>Erstellt</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTeams.map((team) => (
                  <TableRow key={team.id}>
                    <TableCell className="font-medium">{team.name}</TableCell>
                    <TableCell className="text-muted-foreground">{team.captain_name}</TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline">{team.member_count}</Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge className="bg-amber-500/10 text-amber-600">
                        {team.team_elo}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="text-green-600">{team.wins}</span>/
                      <span className="text-red-600">{team.losses}</span>/
                      <span className="text-amber-600">{team.draws || 0}</span>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge className="bg-yellow-500/10 text-yellow-600">
                        <Trophy className="w-3 h-3 mr-1" />
                        {team.tournaments_won}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDateTime(team.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => handleDelete(team.id, team.name)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {filteredTeams.length === 0 && (
              <div className="text-center py-12">
                <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-muted-foreground">Keine Teams gefunden</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
